//#region src/lib/injection-context.ts
var e = class {
	static {
		this.contexts = /* @__PURE__ */ new Map();
	}
	static {
		this.defaultScope = "default";
	}
	static getRegistry(e = this.defaultScope) {
		this.contexts.has(e) || this.contexts.set(e, new l());
		let t = this.contexts.get(e);
		if (!t) throw Error(`Failed to create registry for scope '${e}'`);
		return t;
	}
	static createScope(e) {
		if (this.contexts.has(e)) throw Error(`Scope '${e}' already exists`);
		let t = new l();
		return this.contexts.set(e, t), t;
	}
	static destroyScope(e) {
		let t = this.contexts.get(e);
		t && (t.destroy(), this.contexts.delete(e));
	}
	static async destroyScopeAsync(e) {
		let t = this.contexts.get(e);
		t && (await t.destroyAsync(), this.contexts.delete(e));
	}
	static async clearAllAsync() {
		let e = [], t = Array.from(this.contexts.entries());
		for (let [n, r] of t) try {
			await r.destroyAsync();
		} catch (t) {
			t instanceof p ? e.push(...t.failures) : t instanceof Error && e.push({
				serviceName: `scope:${n}`,
				error: t
			});
		}
		if (this.contexts.clear(), e.length > 0) throw new p(e);
	}
	static setDefaultScope(e) {
		this.defaultScope = e;
	}
	static getActiveScopes() {
		return Array.from(this.contexts.keys());
	}
	static clearAll() {
		let e = Array.from(this.contexts.values());
		for (let t of e) t.destroy();
		this.contexts.clear();
	}
};
function t(t, n, r = {}) {
	let i = e.getRegistry(n), { required: a = !0 } = r;
	try {
		let e = i.getService(t);
		if (e == null) {
			if (a) throw new u(`Service '${t.name}' not found in scope '${n || "default"}' and is required`, t.name);
			return;
		}
		return e;
	} catch (e) {
		throw e instanceof u ? e : new u(`Failed to inject service '${t.name}' from scope '${n || "default"}'`, t.name, e);
	}
}
function n(t, n, ...r) {
	let i = e.getRegistry(n);
	try {
		i.register(t, ...r);
	} catch (e) {
		throw new u(`Failed to register service '${t.name}' in scope '${n || "default"}'`, t.name, e);
	}
}
function r(t, n) {
	let r = e.getRegistry(n);
	try {
		r.registerAsUndefined(t);
	} catch (e) {
		throw new u(`Failed to register service '${t.name}' as undefined in scope '${n || "default"}'`, t.name, e);
	}
}
//#endregion
//#region src/lib/symbol-registry.ts
var i = Symbol.for("@analog-tools/inject:SERVICE_TOKEN");
function a(e) {
	return Symbol(e || "ServiceToken");
}
var o = class {
	static {
		this.tokens = /* @__PURE__ */ new WeakMap();
	}
	static getToken(e) {
		if (!this.tokens.has(e)) {
			let t = a(e.name);
			this.tokens.set(e, t);
		}
		let t = this.tokens.get(e);
		if (!t) throw Error(`Failed to get token for service ${e.name}`);
		return t;
	}
	static setToken(e, t) {
		this.tokens.set(e, t);
	}
};
function s(e) {
	return (t) => {
		let n = e || o.getToken(t);
		return o.setToken(t, n), t.INJECTABLE = !0, t[i] = n, t;
	};
}
//#endregion
//#region src/lib/service-registry.ts
function c() {
	return e.getRegistry();
}
var l = class {
	constructor() {
		this.serviceMap = /* @__PURE__ */ new Map(), this.initializingServices = /* @__PURE__ */ new Set(), this.initializationPromises = /* @__PURE__ */ new Map(), this.serviceNames = /* @__PURE__ */ new Map(), this.initializedServices = /* @__PURE__ */ new Set(), this.destroyed = !1;
	}
	getServiceKey(e) {
		let t = e[i];
		if (!t) throw new f(e.name);
		return this.serviceNames.set(t, e.name), t;
	}
	getServiceName(e) {
		return this.serviceNames.get(e) ?? "Unknown";
	}
	register(e, ...t) {
		let n = this.getServiceKey(e);
		if (this.initializingServices.has(n)) throw new d([e.name]);
		if (!this.serviceMap.has(n) || this.serviceMap.get(n) === void 0) {
			this.initializingServices.add(n);
			try {
				let r = t.length === 0 ? new e() : new e(...t);
				this.serviceMap.set(n, r);
			} finally {
				this.initializingServices.delete(n);
			}
		}
	}
	registerAsUndefined(e) {
		let t = this.getServiceKey(e);
		this.serviceMap.set(t, void 0);
	}
	registerCustomServiceInstance(e, t) {
		let n = this.getServiceKey(e);
		this.serviceNames.set(n, e.name), this.serviceMap.set(n, t);
	}
	getService(e) {
		let t = this.getServiceKey(e);
		return this.serviceMap.has(t) || this.register(e), this.serviceMap.get(t);
	}
	hasService(e) {
		let t = this.getServiceKey(e);
		return this.serviceMap.has(t);
	}
	async ensureAsyncInitialized(e, t) {
		if (this.initializedServices.has(t)) return;
		if (this.initializationPromises.has(t)) {
			let e = this.initializationPromises.get(t);
			if (e) {
				await e;
				return;
			}
		}
		let n = e;
		if (typeof n.initializeAsync != "function") {
			this.initializedServices.add(t);
			let e = Promise.resolve();
			return this.initializationPromises.set(t, e), e;
		}
		let r = n.initializeAsync().then(() => {
			this.initializedServices.add(t);
		}).catch((e) => {
			throw this.initializationPromises.delete(t), e;
		});
		this.initializationPromises.set(t, r), await r;
	}
	async getServiceAsync(e) {
		let t = this.getServiceKey(e);
		this.serviceMap.has(t) || this.register(e);
		let n = this.serviceMap.get(t);
		return n !== void 0 && await this.ensureAsyncInitialized(n, t), n;
	}
	async registerAsync(e, ...t) {
		let n = this.getServiceKey(e);
		(!this.serviceMap.has(n) || this.serviceMap.get(n) === void 0) && this.register(e, ...t);
		let r = this.serviceMap.get(n);
		await this.ensureAsyncInitialized(r, n);
	}
	async destroyAsync() {
		if (this.destroyed) return;
		this.destroyed = !0;
		let e = Array.from(this.initializationPromises.values());
		e.length > 0 && await Promise.allSettled(e);
		let t = [], n = Array.from(this.serviceMap.entries());
		for (let [e, r] of n) {
			if (!this.initializedServices.has(e) || r === void 0) continue;
			let n = r;
			if (typeof n.onDestroy == "function") try {
				await n.onDestroy();
			} catch (n) {
				t.push({
					serviceName: this.getServiceName(e),
					error: n
				});
			}
		}
		if (this.serviceMap.clear(), this.initializingServices.clear(), this.initializationPromises.clear(), this.initializedServices.clear(), this.serviceNames.clear(), t.length > 0) throw new p(t);
	}
	destroy() {
		this.serviceMap.clear(), this.initializingServices.clear(), this.initializationPromises.clear(), this.initializedServices.clear(), this.serviceNames.clear();
	}
}, u = class extends Error {
	constructor(e, t, n) {
		super(e), this.token = t, this.name = "InjectionError", n && (this.cause = n);
	}
}, d = class extends u {
	constructor(e) {
		super(`Circular dependency detected: ${e.join(" -> ")}`), this.name = "CircularDependencyError";
	}
}, f = class extends u {
	constructor(e) {
		super(`Service '${e}' is missing SERVICE_TOKEN. Add @Injectable() decorator to the class. See: packages/inject/docs/migrations/symbol-tokens.md`, e), this.name = "MissingServiceTokenError";
	}
}, p = class extends u {
	constructor(e) {
		let t = e.map((e) => `${e.serviceName}: ${e.error.message}`).join("; ");
		super(`Failed to destroy ${e.length} service(s): ${t}`), this.failures = e, this.name = "AggregateDestructionError";
	}
	getErrors() {
		return this.failures.map((e) => e.error);
	}
	hasFailure(e) {
		return this.failures.some((t) => t.serviceName === e);
	}
};
function m(e, t = {}) {
	let { required: n = !0 } = t;
	try {
		let t = c().getService(e);
		if (t == null) {
			if (n) throw new u(`Service '${e.name}' not found in registry and is required`, e.name);
			return;
		}
		return t;
	} catch (t) {
		throw t instanceof u ? t : new u(`Failed to inject service '${e.name}'`, e.name, t);
	}
}
function h(e, ...t) {
	try {
		c().register(e, ...t);
	} catch (t) {
		throw new u(`Failed to register service '${e.name}'`, e.name, t);
	}
}
function g(e) {
	try {
		c().registerAsUndefined(e);
	} catch (t) {
		throw new u(`Failed to register service '${e.name}' as undefined`, e.name, t);
	}
}
function _(e) {
	try {
		return c().hasService(e);
	} catch {
		return !1;
	}
}
function v(e) {
	return m(e, { required: !1 });
}
async function y(e, t = {}) {
	let { required: n = !0 } = t;
	try {
		let t = await c().getServiceAsync(e);
		if (t == null) {
			if (n) throw new u(`Service '${e.name}' not found in registry and is required`, e.name);
			return;
		}
		return t;
	} catch (t) {
		throw t instanceof u ? t : new u(`Failed to inject async service '${e.name}'`, e.name, t);
	}
}
async function b(e, ...t) {
	try {
		await c().registerAsync(e, ...t);
	} catch (t) {
		throw t instanceof u ? t : new u(`Failed to register async service '${e.name}'`, e.name, t);
	}
}
async function x() {
	return c().destroyAsync();
}
//#endregion
//#region src/lib/inject.testing-util.ts
function S(e, t) {
	c().registerCustomServiceInstance(e, t);
}
function C() {
	e.clearAll();
}
function w(t, n, r) {
	e.getRegistry(r).registerCustomServiceInstance(t, n);
}
function T(t) {
	e.destroyScope(t);
}
//#endregion
export { p as AggregateDestructionError, d as CircularDependencyError, s as Injectable, e as InjectionContext, u as InjectionError, f as MissingServiceTokenError, i as SERVICE_TOKEN, a as createServiceToken, x as destroyAllServicesAsync, _ as hasService, m as inject, y as injectAsync, t as injectScoped, b as registerAsync, S as registerMockService, w as registerMockServiceScoped, h as registerService, g as registerServiceAsUndefined, r as registerServiceAsUndefinedScoped, n as registerServiceScoped, C as resetAllInjections, T as resetScopedInjections, v as tryInject };
